import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, FileSpreadsheet, Loader2, Upload, GitCompare, Download, Trash2, CheckCircle2, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ReconcileHistory } from "@/components/ReconcileHistory";
import { ImportReview } from "@/components/ImportReview";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FIELDS = [
  { key: "date", label: "Date", required: true },
  { key: "amount", label: "Amount (signed)" },
  { key: "debit", label: "Debit / Money out" },
  { key: "credit", label: "Credit / Money in" },
  { key: "description", label: "Description / Memo" },
  { key: "vendor", label: "Provider / Payee" },
  { key: "reference", label: "Reference" },
  { key: "type", label: "Type (debit/credit)" },
];

const ColumnSelect = ({ value, columns, onChange, testId }) => (
  <Select value={value || "none"} onValueChange={v => onChange(v === "none" ? undefined : v)}>
    <SelectTrigger className="mt-1 text-sm" data-testid={testId}><SelectValue placeholder="Select column" /></SelectTrigger>
    <SelectContent>
      <SelectItem value="none">— Not mapped —</SelectItem>
      {columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Section = ({ title, count, tone, open, onToggle, children, testId, actions }) => (
  <Card className={`border ${tone}`} data-testid={testId}>
    <CardContent className="p-0">
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={onToggle} className="flex items-center gap-2 text-sm font-semibold text-stone-900" data-testid={`${testId}-toggle`}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} {title}
          <span className="text-xs font-medium text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">{count}</span>
        </button>
        <div className="ml-auto flex items-center gap-2">{open && actions}</div>
      </div>
      {open && <div className="border-t border-stone-100 max-h-[480px] overflow-y-auto">{children}</div>}
    </CardContent>
  </Card>
);

const RowTable = ({ rows, selected, onToggle, onToggleAll, keyOf, render, testPrefix }) => (
  <table className="w-full text-xs">
    <thead className="bg-stone-50 text-stone-500 sticky top-0">
      <tr>
        {onToggle && <th className="w-10 px-4 py-2 text-left"><Checkbox checked={selected.length > 0 && selected.length === rows.filter(r => !r.locked).length} onCheckedChange={onToggleAll} data-testid={`${testPrefix}-select-all`} /></th>}
        <th className="px-3 py-2 text-left font-medium">Date</th><th className="px-3 py-2 text-left font-medium">Description</th>
        <th className="px-3 py-2 text-left font-medium">Provider</th><th className="px-3 py-2 text-right font-medium">Amount</th>
      </tr>
    </thead>
    <tbody>
      {rows.map(r => {
        const k = keyOf(r); const v = render(r);
        return (
          <tr key={k} className="border-t border-stone-100 hover:bg-stone-50/60" data-testid={`${testPrefix}-row-${k}`}>
            {onToggle && <td className="px-4 py-2">{v.locked ? <Lock className="w-3.5 h-3.5 text-emerald-700" data-testid={`${testPrefix}-locked-${k}`} /> : <Checkbox checked={selected.includes(k)} onCheckedChange={() => onToggle(k)} data-testid={`${testPrefix}-check-${k}`} />}</td>}
            <td className="px-3 py-2 text-stone-600 whitespace-nowrap">{v.date}{v.extra}</td>
            <td className="px-3 py-2 text-stone-800">{v.description || <span className="text-stone-400">—</span>}</td>
            <td className="px-3 py-2 text-stone-600">{v.vendor || <span className="text-stone-400">—</span>}</td>
            <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${v.inflow ? "text-emerald-700" : "text-stone-900"}`}>{v.inflow ? "+" : "−"}{fmt(v.amount)}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

export const ReconcilePanel = ({ stores, categories, isPositiveType, onDataChanged }) => {
  const [file, setFile] = useState(null);
  const [storeId, setStoreId] = useState("all");
  const [columns, setColumns] = useState(null);
  const [mapping, setMapping] = useState({});
  const [sign, setSign] = useState("negative_is_expense");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({ sheet: true, platform: true, matched: false });
  const [selSheet, setSelSheet] = useState([]);
  const [selPlat, setSelPlat] = useState([]);
  const [incomeCat, setIncomeCat] = useState("other_income");
  const [expenseCat, setExpenseCat] = useState("other_expense");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [review, setReview] = useState(null);
  const bumpHistory = () => setHistoryKey(k => k + 1);

  const reset = () => { setFile(null); setColumns(null); setMapping({}); setResult(null); setSelSheet([]); setSelPlat([]); };

  const analyzeFile = async (f) => {
    setFile(f); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await axios.post(`${API}/accounts/import/analyze`, fd);
      setColumns(res.data.columns); setMapping(res.data.auto_mapping || {}); setResult(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read file"); setFile(null); }
    finally { setBusy(false); }
  };

  const runReconcile = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("column_mapping", JSON.stringify(mapping)); fd.append("store_id", storeId); fd.append("sign_convention", sign);
      const runKey = JSON.stringify([storeId, mapping, sign]);
      if (result?.log_id && result.runKey === runKey) fd.append("log_id", result.log_id);
      const res = await axios.post(`${API}/reconcile/run`, fd);
      setResult({ ...res.data, runKey }); setSelSheet([]); setSelPlat([]); bumpHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Reconciliation failed"); }
    finally { setBusy(false); }
  };

  const importSelected = async (all) => {
    const rows = result.sheet_only.filter(r => !r.locked && (all || selSheet.includes(r.row_id)));
    if (!rows.length) return;
    setBusy(true);
    try {
      const typed = rows.map(r => ({ ...r, type: r.direction === "in" ? "income" : "expense" }));
      const sug = (await axios.post(`${API}/transactions/suggest-fields`, { rows: typed.map(r => ({ vendor: r.vendor, amount: r.amount, type: r.type })) })).data;
      const fixedStore = storeId !== "all" ? (storeId === "general" ? null : storeId) : undefined;
      setReview(typed.map((r, i) => {
        const g = sug[i] || {}; const suggested = [];
        const category = g.category || (r.type === "income" ? incomeCat : expenseCat);
        if (g.category && !g.category_from_rule) suggested.push("category");
        let description = r.description;
        if (!description && g.description) { description = g.description; suggested.push("description"); }
        let store_id = fixedStore !== undefined ? fixedStore : null;
        if (fixedStore === undefined && g.store_id) { store_id = g.store_id; suggested.push("store_id"); }
        return { ...r, category, description, store_id, suggested, category_from_rule: !!g.category_from_rule };
      }));
    } catch (e) { toast.error("Could not prepare import"); }
    finally { setBusy(false); }
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const rows = review.map(({ suggested, category_from_rule, type, locked, row_id, ...r }) => ({ ...r, store_id: r.store_id || "general" }));
      const res = await axios.post(`${API}/reconcile/import`, { rows, store_id: storeId, income_category: incomeCat, expense_category: expenseCat, reconcile_log_id: result.log_id });
      setReview(null);
      toast.success(`Imported ${res.data.created} transaction${res.data.created !== 1 ? "s" : ""}${res.data.skipped_locked ? ` · ${res.data.skipped_locked} skipped (locked month)` : ""}`);
      onDataChanged(); await runReconcile();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); setBusy(false); }
  };

  const deleteSelected = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/transactions/bulk-delete`, { ids: selPlat, reconcile_log_id: result.log_id });
      toast.success(`Deleted ${res.data.deleted_count} transaction${res.data.deleted_count !== 1 ? "s" : ""}${res.data.skipped_locked ? ` · ${res.data.skipped_locked} skipped (locked month)` : ""}`);
      onDataChanged(); await runReconcile();
    } catch (e) { toast.error("Delete failed"); setBusy(false); }
  };

  const toggle = (list, setList) => (k) => setList(list.includes(k) ? list.filter(x => x !== k) : [...list, k]);
  const canRun = mapping.date && (mapping.amount || mapping.debit || mapping.credit);

  return (
    <div className="space-y-6" data-testid="reconcile-tab">
      <Card className="border-stone-200/60">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-stone-900 flex items-center gap-2"><GitCompare className="w-5 h-5 text-stone-500" /> Reconcile with Bank Statement</h3>
              <p className="text-sm text-stone-500 mt-1">Upload a bank export (CSV or Excel). Rows are matched by amount with dates within ±3 days. Only platform transactions inside the spreadsheet's date range are compared.</p>
            </div>
            {file && <Button variant="outline" size="sm" onClick={reset} data-testid="reconcile-reset-btn"><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> New file</Button>}
          </div>
          <div className="flex flex-wrap items-end gap-4 mt-5">
            <div>
              <Label className="text-xs text-stone-600">Store</Label>
              <Select value={storeId} onValueChange={setStoreId}>
                <SelectTrigger className="mt-1 w-48 text-sm" data-testid="reconcile-store-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  <SelectItem value="general">General (No Store)</SelectItem>
                  {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 px-4 h-10 rounded-md border border-dashed border-stone-300 text-sm text-stone-600 cursor-pointer hover:border-stone-500 hover:bg-stone-50 transition-colors" data-testid="reconcile-upload-label">
              {busy && !columns ? <Loader2 className="w-4 h-4 animate-spin" /> : file ? <FileSpreadsheet className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
              {file ? file.name : "Choose spreadsheet"}
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="reconcile-file-input"
                onChange={e => { if (e.target.files[0]) analyzeFile(e.target.files[0]); e.target.value = ""; }} />
            </label>
          </div>

          {columns && (
            <div className="mt-6 pt-5 border-t border-stone-100" data-testid="reconcile-mapping">
              <p className="text-sm font-medium text-stone-800 mb-3">Map columns <span className="text-xs text-stone-500 font-normal">— map either a signed Amount column, or Debit/Credit columns</span></p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {FIELDS.map(f => (
                  <div key={f.key}>
                    <Label className="text-xs text-stone-600">{f.label} {f.required && <span className="text-red-500">*</span>}</Label>
                    <ColumnSelect value={mapping[f.key]} columns={columns} onChange={v => setMapping({ ...mapping, [f.key]: v })} testId={`reconcile-map-${f.key}`} />
                  </div>
                ))}
                <div>
                  <Label className="text-xs text-stone-600">Signed amounts mean</Label>
                  <Select value={sign} onValueChange={setSign}>
                    <SelectTrigger className="mt-1 text-sm" data-testid="reconcile-sign-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="negative_is_expense">Negative = money out</SelectItem>
                      <SelectItem value="positive_is_expense">Positive = money out</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={runReconcile} disabled={!canRun || busy} className="mt-4 bg-stone-900 text-white hover:bg-stone-800" data-testid="reconcile-run-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <GitCompare className="w-4 h-4 mr-2" />} Reconcile
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {review && (
        <Card className="border-amber-200"><CardContent className="p-5">
          <h4 className="text-sm font-semibold text-stone-900 mb-3">Review before importing</h4>
          <ImportReview rows={review} setRows={setReview} stores={stores} busy={busy} isPositiveType={isPositiveType}
            categoriesFor={(type) => type === "income" ? (categories.positive || []) : (categories.negative || [])}
            onBack={() => setReview(null)} onConfirm={confirmImport} confirmLabel={`Import ${review.length} transactions`} />
        </CardContent></Card>
      )}

      {result && !review && (
        <ReconcileResults result={result} open={open} setOpen={setOpen} busy={busy}
          selSheet={selSheet} setSelSheet={setSelSheet} selPlat={selPlat} setSelPlat={setSelPlat} toggle={toggle}
          categories={categories} incomeCat={incomeCat} setIncomeCat={setIncomeCat} expenseCat={expenseCat} setExpenseCat={setExpenseCat}
          isPositiveType={isPositiveType} onImport={importSelected} onAskDelete={() => setConfirmDelete(true)} />
      )}

      <ReconcileHistory refreshKey={historyKey} onDataChanged={() => { onDataChanged(); if (result) runReconcile(); }} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="reconcile-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selPlat.length} transaction{selPlat.length !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>These platform transactions were not found in the spreadsheet. They will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reconcile-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={deleteSelected} data-testid="reconcile-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const CatSelect = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-40 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
  </Select>
);

const ReconcileResults = ({ result, open, setOpen, busy, selSheet, setSelSheet, selPlat, setSelPlat, toggle, categories, incomeCat, setIncomeCat, expenseCat, setExpenseCat, isPositiveType, onImport, onAskDelete }) => {
  const c = result.counts;
  const stat = (label, n, cls, tid) => (
    <div className={`rounded-xl border px-4 py-3 ${cls}`} data-testid={tid}><p className="text-xs text-stone-500">{label}</p><p className="text-2xl font-semibold text-stone-900 mt-0.5">{n}</p></div>
  );
  return (
    <div className="space-y-4" data-testid="reconcile-results">
      <p className="text-xs text-stone-500" data-testid="reconcile-range">Spreadsheet covers {result.range.start} → {result.range.end} · {c.sheet_rows} rows</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {stat("Matched", c.matched, "border-emerald-200 bg-emerald-50/50", "reconcile-count-matched")}
        {stat("In spreadsheet, not in platform", c.sheet_only, "border-amber-200 bg-amber-50/50", "reconcile-count-sheet-only")}
        {stat("In platform, not in spreadsheet", c.platform_only, "border-red-200 bg-red-50/50", "reconcile-count-platform-only")}
      </div>

      <Section title="In spreadsheet, not in platform" count={c.sheet_only} tone="border-amber-200" open={open.sheet} onToggle={() => setOpen({ ...open, sheet: !open.sheet })} testId="reconcile-sheet-only"
        actions={c.sheet_only > 0 && <>
          <span className="text-[10px] text-stone-500">Money in →</span>
          <CatSelect value={incomeCat} onChange={setIncomeCat} options={categories.positive || []} testId="reconcile-income-cat" />
          <span className="text-[10px] text-stone-500">Money out →</span>
          <CatSelect value={expenseCat} onChange={setExpenseCat} options={categories.negative || []} testId="reconcile-expense-cat" />
          <Button size="sm" variant="outline" className="h-8" disabled={busy || !selSheet.length} onClick={() => onImport(false)} data-testid="reconcile-import-selected-btn"><Download className="w-3.5 h-3.5 mr-1" /> Import selected ({selSheet.length})</Button>
          <Button size="sm" className="h-8 bg-stone-900 text-white" disabled={busy} onClick={() => onImport(true)} data-testid="reconcile-import-all-btn">Import all</Button>
        </>}>
        {c.sheet_only === 0 ? <p className="text-sm text-stone-400 text-center py-6">Everything in the spreadsheet is already in the platform</p> :
          <RowTable rows={result.sheet_only} selected={selSheet} keyOf={r => r.row_id} testPrefix="sheet"
            onToggle={toggle(selSheet, setSelSheet)} onToggleAll={() => { const ok = result.sheet_only.filter(r => !r.locked); setSelSheet(selSheet.length === ok.length ? [] : ok.map(r => r.row_id)); }}
            render={r => ({ ...r, inflow: r.direction === "in" })} />}
        {c.sheet_only > 0 && <p className="text-[11px] text-stone-400 px-4 py-2 border-t border-stone-100">Category rules for matching providers override the defaults above.</p>}
      </Section>

      <Section title="In platform, not in spreadsheet" count={c.platform_only} tone="border-red-200" open={open.platform} onToggle={() => setOpen({ ...open, platform: !open.platform })} testId="reconcile-platform-only"
        actions={c.platform_only > 0 && <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" disabled={busy || !selPlat.length} onClick={onAskDelete} data-testid="reconcile-delete-selected-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete selected ({selPlat.length})</Button>}>
        {c.platform_only === 0 ? <p className="text-sm text-stone-400 text-center py-6">Every platform transaction in this date range is in the spreadsheet</p> :
          <RowTable rows={result.platform_only} selected={selPlat} keyOf={t => t.id} testPrefix="platform"
            onToggle={toggle(selPlat, setSelPlat)} onToggleAll={() => { const ok = result.platform_only.filter(t => !t.locked); setSelPlat(selPlat.length === ok.length ? [] : ok.map(t => t.id)); }}
            render={t => ({ ...t, inflow: isPositiveType(t.type) })} />}
      </Section>

      <Section title="Matched" count={c.matched} tone="border-emerald-200" open={open.matched} onToggle={() => setOpen({ ...open, matched: !open.matched })} testId="reconcile-matched">
        {c.matched === 0 ? <p className="text-sm text-stone-400 text-center py-6">No matches</p> :
          <RowTable rows={result.matched} selected={[]} keyOf={m => m.txn.id} testPrefix="matched"
            render={m => ({ ...m.sheet, description: m.sheet.description || m.txn.description, vendor: m.txn.vendor || m.sheet.vendor, inflow: m.sheet.direction === "in",
              extra: m.day_diff > 0 ? <span className="ml-1.5 text-[10px] text-amber-600">(platform: {m.txn.date}, {m.day_diff}d off)</span> : <CheckCircle2 className="inline w-3 h-3 ml-1.5 text-emerald-500" /> })} />}
      </Section>
    </div>
  );
};
